import { useState, useEffect, useRef, Suspense } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import {
  Package, Users, LogOut, Menu, Search, FileText,
  BarChart3, Waves, ChevronLeft, ChevronRight,
  BookOpen, UserCheck, Crown, Building, Palette,
  Table, Clock, PanelLeftClose, PanelLeftOpen, X
} from 'lucide-react';
import SelectorEmpresaNOBUTTON from './SelectorEmpresaNOBUTTON';
import SuperAdminCompanySelector from './SuperAdminCompanySelector';
import ThemeCustomizer from './ThemeCustomizer';
import ConfidentialityModal from './ConfidentialityModal';
import { useEmpresa } from '../contexts/EmpresaContext';
import '../styles/Layout.css';

// Mini 3D boat for sidebar
function SidebarBoatModel() {
  const meshRef = useRef();
  const { scene } = useGLTF('/paper_boat.glb', true, true);

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.008;
    }
  });

  return (
    <primitive
      ref={meshRef}
      object={scene}
      scale={[58, 58, 58]}
      position={[0.4, -0.35, 0]}
    />
  );
}

const Layout = ({ children }) => {
  const detectMobile = () => {
    return window.innerWidth <= 768 ||
           (window.innerWidth <= 950 && window.innerHeight <= 600);
  };

  const isMobileInitial = detectMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobileInitial);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileInitial);
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSuperAdmin } = useSelector(state => state.auth);
  const { empresaActiva, cambiarEmpresa } = useEmpresa();
  const dispatch = useDispatch();
  const lastPathRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = detectMobile();
      setIsMobile(mobile);
      if (!mobile && !sidebarOpen) setSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Block body scroll on mobile sidebar open
  useEffect(() => {
    const mobile = detectMobile();
    if (mobile && sidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.touchAction = 'none';
    } else if (mobile) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    };
  }, [sidebarOpen]);

  // Hide header when theme modal open on mobile
  useEffect(() => {
    const mobile = detectMobile();
    const header = document.querySelector('.layout-header');
    if (mobile) {
      if (themeModalOpen) {
        if (header) header.style.display = 'none';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      } else {
        if (header) header.style.display = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    }
    return () => {
      if (header) header.style.display = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [themeModalOpen]);

  // Apply theme colors
  useEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;

    const applyColors = () => {
      const savedPrimary = localStorage.getItem('theme-primary-color') || '#c4fffc';
      const savedSecondary = localStorage.getItem('theme-secondary-color') || '#000000';

      const root = document.documentElement;
      root.style.setProperty('--theme-primary', savedPrimary);
      root.style.setProperty('--theme-secondary', savedSecondary);

      const adjustColor = (color, percent) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
          (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
          (B < 255 ? B < 1 ? 0 : B : 255))
          .toString(16).slice(1);
      };

      root.style.setProperty('--theme-primary-light', adjustColor(savedPrimary, 20));
      root.style.setProperty('--theme-primary-dark', adjustColor(savedPrimary, -20));
      root.style.setProperty('--theme-primary-darker', adjustColor(savedPrimary, -30));

      const applyThemeColors = () => {
        document.querySelectorAll('.layout-content').forEach(el => { el.style.background = savedPrimary; });
        document.querySelectorAll('.dashboard-content').forEach(el => { el.style.background = savedPrimary; });
        document.querySelectorAll('.cotizaciones-container, .clientes-container, .equipo-container, .ordenes-venta-container, .ordenes-compra-container, .proveedores-container, .finanzas-container, .calendario-container, .manual-container, .perfil-container, .configuracion-container, .urgencias-container, .transito-container, .empresa-container, .super-admin-container, .configuracion-empresa-container').forEach(el => {
          el.style.background = savedPrimary;
        });
      };

      applyThemeColors();
      setTimeout(applyThemeColors, 100);
    };

    applyColors();

    const handleThemeChange = () => applyColors();
    window.addEventListener('theme-color-change', handleThemeChange);
    return () => window.removeEventListener('theme-color-change', handleThemeChange);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await dispatch(logout());
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      }
      window.location.href = '/login';
    } catch (error) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  };

  const shouldShowCompanyMenu = !isSuperAdmin || (isSuperAdmin && empresaActiva);
  const userPermissions = empresaActiva?.permissions?.sections || empresaActiva?.permisos?.sections || {};
  const userRole = empresaActiva?.rolUsuario || empresaActiva?.role;

  const allMenuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/dashboard', permission: 'dashboard' },
    { icon: Table, label: 'Maestro', path: '/matriz', permission: 'maestro' },
    { icon: Users, label: 'Clientes', path: '/clientes', permission: 'clientes' },
    { icon: Package, label: 'Proveedores', path: '/proveedores', permission: 'proveedores' },
    { icon: UserCheck, label: 'Equipo', path: '/equipo', permission: 'equipo' },
    { icon: Building, label: 'Empresa', path: '/empresa', permission: 'empresa' },
    { icon: FileText, label: 'Documentos', path: '/documentos', permission: 'documentos' },
    { icon: Clock, label: 'Historial', path: '/historial', permission: 'historial' },
  ];

  const menuItems = [
    ...(shouldShowCompanyMenu ?
      allMenuItems.filter(item => {
        if (userRole === 'admin' || userRole === 'editor' || isSuperAdmin) return true;
        return userPermissions[item.permission] === true;
      })
    : []),
    { icon: BookOpen, label: 'Manual', path: '/manual' },
    ...(isSuperAdmin ? [{
      icon: Crown, label: 'Super Admin', path: '/super-admin', superAdmin: true
    }] : []),
  ];

  const handleMenuItemClick = (item) => {
    if (isMobile) setSidebarOpen(false);
    if (item?.superAdmin) cambiarEmpresa(null);
  };

  const isCollapsed = sidebarCollapsed && !isMobile;

  return (
    <div className={`layout-container ${isCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar ${sidebarOpen ? '' : 'sidebar-closed'} ${isCollapsed ? 'collapsed' : ''}`}>
        {/* 3D Boat Header */}
        <div className="sidebar-header">
          {isMobile && (
            <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)}>
              <X size={20} />
            </button>
          )}
          <div className="boat-3d-container">
            <Suspense fallback={
              <div className="boat-fallback">
                <img src="/paper-boat-icon.png" alt="Paper Boat" className="sidebar-boat-icon" />
              </div>
            }>
              <Canvas
                camera={{ position: [0, 1, 12], fov: 50 }}
                style={{ background: 'transparent' }}
                gl={{ alpha: true, antialias: true }}
              >
                <ambientLight intensity={1.2} />
                <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={2} />
                <pointLight position={[-10, -10, -10]} intensity={1.5} />
                <directionalLight position={[0, 10, 5]} intensity={1.5} />
                <SidebarBoatModel />
              </Canvas>
            </Suspense>
          </div>
          {!isCollapsed && <h2 className="brand-name">PAPER BOAT</h2>}
        </div>

        {/* Glassmorphic nav section */}
        <nav className="sidebar-nav">
          <div className="nav-section-label">{!isCollapsed && 'MENU'}</div>
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path ||
                           (location.pathname === '/' && item.path === '/dashboard');

            return (
              <Link
                key={index}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''} ${item.superAdmin ? 'super-admin' : ''}`}
                onClick={() => handleMenuItemClick(item)}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
                data-tooltip={item.label}
              >
                <div className="nav-icon-wrapper">
                  <item.icon size={isCollapsed ? 22 : 20} strokeWidth={isActive ? 2.5 : 1.8} />
                </div>
                {!isCollapsed && <span className="nav-label">{item.label}</span>}
                {isActive && <div className="active-glow" />}

                {/* Tooltip when collapsed */}
                {isCollapsed && hoveredItem === index && (
                  <div className="nav-tooltip">{item.label}</div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {!isMobile && (
            <button className="sidebar-collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              {!isCollapsed && <span>Colapsar</span>}
            </button>
          )}
          <button
            className="logout-btn"
            onClick={handleLogout}
            onMouseEnter={() => setHoveredItem('logout')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <LogOut size={18} />
            {!isCollapsed && <span>Cerrar Sesion</span>}
            {isCollapsed && hoveredItem === 'logout' && (
              <div className="nav-tooltip">Cerrar Sesion</div>
            )}
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="layout-header" style={isMobile ? { position: 'relative', background: 'white' } : {}}>
          {(!sidebarOpen || (isMobile && !sidebarOpen)) && (
            <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
              <Menu size={24} />
            </button>
          )}

          {!isMobile && (
            <div className="header-search">
              <Search size={18} />
              <input type="text" placeholder="Buscar..." />
            </div>
          )}

          <div className="header-actions" style={isMobile ? { display: 'contents' } : {}}>
            {!isSuperAdmin && empresaActiva && <SelectorEmpresaNOBUTTON />}
            {isSuperAdmin && <SuperAdminCompanySelector />}

            <button className="theme-btn" onClick={() => setThemeModalOpen(true)} title="Personalizar Colores">
              <Palette size={20} />
            </button>

            <div className="user-menu" onClick={() => navigate('/perfil')}>
              <div className="user-avatar">
                <Waves size={isMobile ? 16 : 18} />
              </div>
              {!isMobile && (
                <div className="user-info">
                  <span className="user-name">
                    {user ? `${user.firstName} ${user.lastName}` : (localStorage.getItem('username') || 'Usuario')}
                  </span>
                  <span className="user-role">
                    {isSuperAdmin ? 'Super Admin' : (empresaActiva?.nombreFantasia || empresaActiva?.tradeName || '')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="layout-content">
          {children}
        </div>
      </main>

      <ThemeCustomizer isOpen={themeModalOpen} onClose={() => setThemeModalOpen(false)} />
      <ConfidentialityModal user={user} isSuperAdmin={isSuperAdmin} />
    </div>
  );
};

export default Layout;
